<script setup lang="ts">
/**
 * Four stacked sections — Ingress, Egress, External hosts, Jobs — over one date range, one refresh
 * timer and one view context.
 *
 * Stacked rather than a uniform chart grid: jobs are grouped by definition *and* by queue, and
 * measure depth and saturation rather than latency, so they do not fit a calls-and-latency grid.
 * External hosts fit it no better — one latency chart, no volume chart, sorted by slowness.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { InstantRangeInput, LineChart, type InstantRangeValue } from '@hantera/design-system'
import { useEvents, useLocalization } from '@hantera/portal-app'
import useStatisticsViewContext from './use-statistics-view-context'

const t = useLocalization('monitoring')
const events = useEvents()

const statistics = useStatisticsViewContext()

const sections = statistics.sections

const dateRange = computed<InstantRangeValue>({
  get: () => ({
    from: statistics.dateRange.from,
    to: statistics.dateRange.to,
    rolling: statistics.rolling ?? undefined,
  }),
  set: (value: InstantRangeValue) => {
    statistics.setRange(value)
    statistics.refresh()
  },
})

const selectedIngress = ref()
const selectedEgress = ref()
const selectedEgressHost = ref()
const selectedJob = ref();
const selectedJobQueue = ref();

const updatedLabel = computed(() => {
  const at = statistics.loadState.lastRefreshedAt
  if (!at) return ''


  return t('statistics.updatedAt', {
    time: at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  })
})

/** The design system is deliberately unit-agnostic, so the unit is supplied per chart. */
const formatMs = (value: number) => `${Math.round(value)} ms`
const formatCount = (value: number) => value.toLocaleString()

function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`
}

function formatSaturation(value: number | null): string {
  return value == null ? '—' : `${Math.round(value * 100)}%`
}

/**
 * A queue whose workers are all busy while nothing completes is wedged, which is the case this
 * section exists to surface. Depth alone does not say it; saturation is what separates wedged from
 * starved.
 */
function queueLegendClass(row: { queue: string, saturation: number | null; completed: number; dueDepth: number }) {
  const result = []
  if (row.dueDepth > 0 && row.completed === 0) {
    result.push(row.saturation === 1 ? 'danger' : 'warning')
  }
  if (selectedJobQueue.value === row.queue) {
    result.push('selected')
  }
  return result
}

let refreshInterval: ReturnType<typeof setInterval> | null = null
let alignmentTimeout: ReturnType<typeof setTimeout> | null = null
const unsubscribes: (() => void)[] = []

function subscribe(path: string, event: string, handler: (data: any) => void) {
  if (!events.available) return

  unsubscribes.push(
    events.subscribe({
      path,
      events: [event as any],
      onEvent: (e: any) => {
        if (e.eventType === event) {
          handler(e.data)
        }
      },
    }),
  )
}

function tickIfVisible() {
  if (document.visibilityState === 'visible') {
    statistics.tick()
  }
}

/**
 * Buckets are written per minute, so a poll landing mid-minute always renders a newest bucket that
 * is still filling — a dip on the right that reads as traffic falling off. Waiting for the boundary
 * (plus enough slack for the flush to land) costs no extra requests and makes the right edge honest.
 */
const BUCKET_FLUSH_SLACK_MS = 5_000

function startAlignedPolling() {
  const now = Date.now()
  const nextBoundary = Math.ceil(now / 60_000) * 60_000 + BUCKET_FLUSH_SLACK_MS

  alignmentTimeout = setTimeout(() => {
    alignmentTimeout = null
    tickIfVisible()
    refreshInterval = setInterval(tickIfVisible, 60_000)
  }, nextBoundary - now)
}

onMounted(() => {
  statistics.refresh()

  if (sections.egress) {
    subscribe('egresses', 'egressStatistics', (data) => statistics.handleEgressEvent(data))
    subscribe('egress-hosts', 'egressHostStatistics', (data) => statistics.handleHostEvent(data))
  }

  if (sections.ingress) {
    subscribe('ingresses', 'ingressStatistics', (data) => statistics.handleIngressEvent(data))
  }

  if (sections.jobs) {
    subscribe('job-definitions', 'jobStatistics', (data) => statistics.handleJobEvent(data))
    subscribe('job-queues', 'jobQueueStatistics', (data) => statistics.handleQueueEvent(data))
  }

  // Live events cover the open bucket; the poll catches the aggregates that only the API computes,
  // advances a rolling window, and recovers from a dropped subscription.
  startAlignedPolling()
})


onUnmounted(() => {
  if (alignmentTimeout) {
    clearTimeout(alignmentTimeout)
    alignmentTimeout = null
  }
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
  for (const unsubscribe of unsubscribes) {
    unsubscribe()
  }
})

function toggleSelectedIngress(value: string) {
  if (selectedIngress.value === value) {
    selectedIngress.value = undefined
  } else {
    selectedIngress.value = value
  }
}
function toggleSelectedEgress(value: string) {
  if (selectedEgress.value === value) {
    selectedEgress.value = undefined
  } else {
    selectedEgress.value = value
  }
}
function toggleSelectedEgressHost(value: string) {
  if (selectedEgressHost.value === value) {
    selectedEgressHost.value = undefined
  } else {
    selectedEgressHost.value = value
  }
}
function toggleSelectedJob(value: string) {
  if (selectedJob.value === value) {
    selectedJob.value = undefined
  } else {
    selectedJob.value = value
  }
}
function toggleSelectedJobQueue(value: string) {
  if (selectedJobQueue.value === value) {
    selectedJobQueue.value = undefined
  } else {
    selectedJobQueue.value = value
  }
}

</script>

<template>
  <div class="statistics-tab">
    <div class="toolbar">
      <label>{{ t('statistics.dateRange') }}</label>
      <InstantRangeInput v-model="dateRange" rolling />
      <span class="spacer" />
      <span v-if="statistics.loadState.error" class="error">
        {{ statistics.loadState.error }}
      </span>

      <span v-else-if="updatedLabel" class="muted">{{ updatedLabel }}</span>
    </div>

    <div class="statistics-content">

      <!-- Each section renders only when the caller holds its :statistics permission. A section they
          cannot read is omitted, not shown empty. -->
      <section v-if="sections.ingress" class="box p">
        <h3>{{ t('statistics.ingress') }}</h3>
        <div class="charts">
          <LineChart
            :series="statistics.ingressRequestSeries"
            :title="t('statistics.requests')"
            :format-value="formatCount"
            :height="140"
            v-model:selected="selectedIngress"
            class="box-content p"
          />
          <LineChart
            :series="statistics.ingressLatencySeries"
            :title="t('statistics.p95Latency')"
            :format-value="formatMs"
            :height="140"
            v-model:selected="selectedIngress"
            class="box-content p"
          />
        </div>
        <table class="box-content p">
          <thead>
            <tr>
              <th>{{ t('statistics.columns.ingress') }}</th>
              <th class="num">{{ t('statistics.columns.requests') }}</th>
              <th class="num">4xx</th>
              <th class="num">5xx</th>
              <th class="num">{{ t('statistics.columns.p95') }}</th>
              <th class="num">{{ t('statistics.columns.max') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in statistics.ingressRows"
              :key="row.ingressId"
              class="legend"
              :class="{
                'selected': row.ingressId === selectedIngress
              }"
              @click="() => toggleSelectedIngress(row.ingressId)"
            >
              <td>{{ row.ingressId }}</td>
              <td class="num">{{ formatCount(row.requests) }}</td>
              <td class="num">{{ formatCount(row.clientErrors) }}</td>
              <td class="num" :class="{ bad: row.serverErrors > 0 }">
                {{ formatCount(row.serverErrors) }}
              </td>
              <td class="num">{{ formatDuration(row.p95DurationMs) }}</td>
              <td class="num">{{ formatDuration(row.maxDurationMs) }}</td>
            </tr>
            <tr v-if="statistics.ingressRows.length === 0">
              <td colspan="6" class="muted">{{ t('statistics.noData') }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section v-if="sections.egress" class="box p">
        <h3>{{ t('statistics.egress') }}</h3>
        <div class="charts">
          <LineChart
            :series="statistics.egressCallSeries"
            :title="t('statistics.calls')"
            :format-value="formatCount"
            :height="140"
            v-model:selected="selectedEgress"
            class="box-content p"
          />
          <LineChart
            :series="statistics.egressLatencySeries"
            :title="t('statistics.p95Latency')"
            :format-value="formatMs"
            :height="140"
            v-model:selected="selectedEgress"
            class="box-content p"
          />
        </div>
        <table class="box-content p">
          <thead>
            <tr>
              <th>{{ t('statistics.columns.egress') }}</th>
              <th class="num">{{ t('statistics.columns.calls') }}</th>
              <th class="num">{{ t('statistics.columns.cacheHits') }}</th>
              <th class="num">{{ t('statistics.columns.failed') }}</th>
              <th class="num">{{ t('statistics.columns.p95') }}</th>
              <th class="num">{{ t('statistics.columns.max') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in statistics.egressRows"
              :key="row.egressKey"
              class="legend"
              :class="{
                'selected': row.egressKey === selectedEgress
              }"
              @click="() => toggleSelectedEgress(row.egressKey)"
            >
              <td>{{ row.egressKey }}</td>
              <td class="num">{{ formatCount(row.total) }}</td>
              <td class="num">{{ formatCount(row.cacheHits) }}</td>
              <td class="num" :class="{ bad: row.failed > 0 }">{{ formatCount(row.failed) }}</td>
              <td class="num">{{ formatDuration(row.p95DurationMs) }}</td>
              <td class="num">{{ formatDuration(row.maxDurationMs) }}</td>
            </tr>
            <tr v-if="statistics.egressRows.length === 0">
              <td colspan="6" class="muted">{{ t('statistics.noData') }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Shares egresses:statistics with the section above, so the two appear and disappear
          together. Host counts will not match egress counts: an egress that fetches a token and then
          posts is one egress call and two host calls. -->
      <section v-if="sections.egress" class="box p">
        <h3>{{ t('statistics.hosts') }}</h3>
        <p class="hint">{{ t('statistics.hostsHint') }}</p>
        <div class="charts">
          <LineChart
            :series="statistics.hostCallSeries"
            :title="t('statistics.calls')"
            :format-value="formatCount"
            :height="150"
            v-model:selected="selectedEgressHost"
            class="box-content p"
          />
          <LineChart
            :series="statistics.hostLatencySeries"
            :title="t('statistics.p95Latency')"
            :format-value="formatMs"
            :height="150"
            v-model:selected="selectedEgressHost"
            class="box-content p"
          />
        </div>
        <table class="box-content p">
          <thead>
            <tr>
              <th>{{ t('statistics.columns.host') }}</th>
              <th>{{ t('statistics.columns.transport') }}</th>
              <th class="num">{{ t('statistics.columns.calls') }}</th>
              <th class="num">{{ t('statistics.columns.failed') }}</th>
              <th class="num">{{ t('statistics.columns.p95') }}</th>
              <th class="num">{{ t('statistics.columns.max') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in statistics.hostRows"
              :key="`${row.transport}/${row.host}`"
              class="legend"
              :class="{
                'selected': row.host === selectedEgressHost
              }"
              @click="() => toggleSelectedEgressHost(row.host)"
            >
              <td>{{ row.host }}</td>
              <td>{{ row.transport }}</td>
              <td class="num">{{ formatCount(row.total) }}</td>
              <td class="num" :class="{ bad: row.failed > 0 }">{{ formatCount(row.failed) }}</td>
              <td class="num">{{ formatDuration(row.p95DurationMs) }}</td>
              <td class="num">{{ formatDuration(row.maxDurationMs) }}</td>
            </tr>
            <tr v-if="statistics.hostRows.length === 0">
              <td colspan="6" class="muted">{{ t('statistics.noData') }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section v-if="sections.jobs" class="box p">
        <h3>{{ t('statistics.jobs') }}</h3>
        <div class="charts">
          <LineChart
            :series="statistics.jobOutcomeSeries"
            :title="t('statistics.completed')"
            :format-value="formatCount"
            :height="140"
            v-model:selected="selectedJob"
            class="box-content p"
          />
          <LineChart
            :series="statistics.jobLatencySeries"
            :title="t('statistics.p95ExecutionTime')"
            :format-value="formatMs"
            :height="140"
            v-model:selected="selectedJob"
            class="box-content p"
          />
        </div>
        <table class="box-content p">
          <thead>
            <tr>
              <th>{{ t('statistics.columns.definition') }}</th>
              <th class="num">{{ t('statistics.columns.successful') }}</th>
              <th class="num">{{ t('statistics.columns.failed') }}</th>
              <th class="num">{{ t('statistics.columns.due') }}</th>
              <th class="num">{{ t('statistics.columns.scheduled') }}</th>
              <th class="num">{{ t('statistics.columns.p95') }}</th>
              <th class="num">{{ t('statistics.columns.max') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in statistics.jobRows"
              :key="row.jobDefinitionId"
              class="legend"
              :class="{
                'selected': row.jobDefinitionId === selectedJob
              }"
              @click="() => toggleSelectedJob(row.jobDefinitionId)"
            >
              <td>{{ row.jobDefinitionId }}</td>
              <td class="num">{{ formatCount(row.successful) }}</td>
              <td class="num" :class="{ bad: row.failed > 0 }">{{ formatCount(row.failed) }}</td>
              <td class="num">{{ formatCount(row.due) }}</td>
              <td class="num">{{ formatCount(row.scheduled) }}</td>
              <td class="num">{{ formatDuration(row.p95ExecutionMs) }}</td>
              <td class="num">{{ formatDuration(row.maxExecutionMs) }}</td>
            </tr>
            <tr v-if="statistics.jobRows.length === 0">
              <td colspan="7" class="muted">{{ t('statistics.noData') }}</td>
            </tr>
          </tbody>
        </table>

        <LineChart
          :series="statistics.queueDepthSeries"
          :title="t('statistics.queueDepth')"
          :format-value="formatCount"
          :height="140"
          v-model:selected="selectedJobQueue"
          class="box-content p"
        />
        <table class="box-content p">
          <thead>
            <tr>
              <th>{{ t('statistics.columns.queue') }}</th>
              <th class="num">{{ t('statistics.columns.due') }}</th>
              <th class="num">{{ t('statistics.columns.ready') }}</th>
              <th class="num">{{ t('statistics.columns.busy') }}</th>
              <th class="num">{{ t('statistics.columns.workers') }}</th>
              <th class="num">{{ t('statistics.columns.saturation') }}</th>
              <th class="num">{{ t('statistics.columns.completed') }}</th>
              <th class="num">{{ t('statistics.columns.failed') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in statistics.queueRows"
              :key="row.queue"
              class="legend"
              :class="queueLegendClass(row)"
              @click="() => toggleSelectedJobQueue(row.queue)"
            >
              <td>{{ row.queue }}</td>
              <td class="num">{{ formatCount(row.dueDepth) }}</td>
              <td class="num">{{ formatCount(row.readyDepth) }}</td>
              <td class="num">{{ formatCount(row.busyWorkers) }}</td>
              <td class="num">{{ formatCount(row.workers) }}</td>
              <td class="num">{{ formatSaturation(row.saturation) }}</td>
              <td class="num">{{ formatCount(row.completed) }}</td>
              <td class="num" :class="{ bad: row.failed > 0 }">{{ formatCount(row.failed) }}</td>
            </tr>
            <tr v-if="statistics.queueRows.length === 0">
              <td colspan="8" class="muted">{{ t('statistics.noData') }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  </div>
</template>

<style scoped>
.statistics-tab {
  display: grid;
  grid-template-rows: min-content auto;
  height: 100%;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar label {
  font-size: 12px;
  color: hsl(var(--color-40));
}

.statistics-content {
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-2);
}

.spacer {
  flex: 1;
}

section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
}

h4 {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: hsl(var(--color-40));
  border-top: 1px solid hsl(var(--color-90));
  padding-top: 8px;
}

.charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.hint {
  margin: 0;
  font-size: 11px;
  color: hsl(var(--color-40));
}


.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.bad {
  color: hsl(var(--color-hue-red) 60% 45%);
}

.muted {
  color: hsl(var(--color-40));
  font-size: 12px;
}

.error {
  color: hsl(var(--color-hue-red) 60% 45%);
  font-size: 12px;
}

tr.warning td {
  background-color: hsl(45 80% 94%);
}

tr.danger td {
  background-color: hsl(var(--color-hue-red) 75% 95%);
}

tr.legend:hover {
  background-color: var(--surface-hover);
}

tr.legend.selected {
  color: var(--primary-foreground);
  background-color: var(--primary);
}

tr.legend.selected:hover {
  background-color: var(--primary-hover);
}
</style>
